/**
 * Versioned canonical call, effect-reference, and state types. Every other
 * package imports its address/hash/amount primitives from here so a 20-byte
 * address is validated the same way in a manifest as it is in a receipt.
 */
import { z } from "zod";
export const SCHEMA_VERSION_1_0 = "1.0";
export const HexAddressSchema = z
    .string()
    .regex(/^0x[0-9a-fA-F]{40}$/, "must be a 20-byte 0x-prefixed hex address");
export const HexHashSchema = z
    .string()
    .regex(/^0x[0-9a-fA-F]{64}$/, "must be a 32-byte 0x-prefixed hex hash");
export const HexDataSchema = z
    .string()
    .regex(/^0x([0-9a-fA-F]{2})*$/, "must be even-length 0x-prefixed hex data");
export const HexSelectorSchema = z
    .string()
    .regex(/^0x[0-9a-fA-F]{8}$/, "must be a 4-byte 0x-prefixed function selector");
/**
 * A base-10 integer string, never a `number`. Native/token amounts routinely
 * exceed `Number.MAX_SAFE_INTEGER`.
 */
export const AmountSchema = z.string().regex(/^\d+$/, "must be a base-10 integer string");
export const GitCommitShaSchema = z
    .string()
    .regex(/^[0-9a-f]{40}$/i, "must be a 40-character git commit SHA");
/** A UTC ISO-8601 timestamp, e.g. `2026-08-13T07:15:00.000Z`. */
export const IsoTimestampSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/, "must be a UTC ISO-8601 timestamp ending in Z");
export const CallTypeSchema = z.enum(["CALL", "STATICCALL", "DELEGATECALL", "CREATE", "CREATE2"]);
export const ResultClassSchema = z.enum(["return", "revert", "halt"]);
export const StorageAccessSchema = z.object({
    slot: HexHashSchema,
    value: HexHashSchema,
    decoded: z.string().optional(),
});
export const EventRefSchema = z.object({
    address: HexAddressSchema,
    topics: z.array(HexHashSchema).max(4),
    data: HexDataSchema,
    name: z.string().optional(),
});
/** Lightweight pointer from a call node to a fuller record in `effects.json`. */
export const EffectRefSchema = z.object({
    id: z.string().min(1),
    type: z.enum(["native-transfer", "token-transfer", "event", "storage-write"]),
});
export const CallResultSchema = z.object({
    class: ResultClassSchema,
    label: z.string().optional(),
});
/**
 * A flat, addressable node: `children` holds sibling `id`s rather than
 * nesting, so the aligner can look up either side by id in O(1).
 */
export const CallNodeSchema = z.object({
    id: z.string().min(1),
    parentId: z.string().min(1).nullable(),
    depth: z.number().int().nonnegative(),
    contractRole: z.string().optional(),
    address: HexAddressSchema,
    codeIdentity: z.string().optional(),
    selector: HexSelectorSchema.optional(),
    functionName: z.string().optional(),
    callType: CallTypeSchema,
    semanticPath: z.string().min(1),
    msgValue: AmountSchema,
    effects: z.array(EffectRefSchema),
    storageReads: z.array(StorageAccessSchema),
    storageWrites: z.array(StorageAccessSchema),
    events: z.array(EventRefSchema),
    result: CallResultSchema,
    children: z.array(z.string().min(1)),
});
/** The `trace.json` artifact written per side (vulnerable/patched) by EL-033. */
export const TraceSchema = z
    .object({
    schemaVersion: z.literal(SCHEMA_VERSION_1_0),
    caseId: z.string().min(1),
    side: z.enum(["vulnerable", "patched"]),
    rootId: z.string().min(1),
    nodes: z.array(CallNodeSchema).min(1),
})
    .refine((trace) => trace.nodes.some((node) => node.id === trace.rootId), {
    message: "rootId must reference one of the trace's own nodes",
    path: ["rootId"],
});
//# sourceMappingURL=schema.js.map